import { Action, ThunkDispatch } from '@reduxjs/toolkit'
import { ClientOffer, Offer, SavedOffer } from 'app/types/types'
import {
  getUnsavedOffersPromise,
  updateOfferPromise,
  getOffersPromise,
  getOfferByOpportunityIdPromise
} from 'app/app/(client)/db/db'
import Store, { RootState } from '../../Store'
import { saveOffer } from 'app/services/client/client-api'
import { isOfferFinalized, offerIsCompleted } from 'app/app/common/offer-state-predicates'
import moment from 'moment'
import { updateOfferAsync } from '../currentOffer/update-offer-async'
import { setUnsavedOfferCount } from '../currentOffer/unsaved-offer-slice'
import { setSyncInProgress } from './sync-slice'
import { wrapCreateAsyncThunk } from 'app/app/(client)/utils/async-handler'
import { setOfferData } from '../currentOffer/current-offer-slice'
import { unsavedOffersCount } from 'app/app/(client)/nemo/offer'
import { ApiError } from 'app/services/common-error-utils'

// ClientServerOffer type is used when communicating between
// Browser and Server. It represents a pair where clientOffer is
// what is sent by the client and serverOffer is what the Server returned
interface ClientServerOffer {
  clientOffer: ClientOffer
  serverOffer: SavedOffer
}

const syncOfferHandler = async (_: void, thunkAPI: any) => {
  const { dispatch, getState } = thunkAPI
  const state: RootState = getState()
  if (state.syncInProgress.data) {
    console.info('Sync already in progress. Skipping.')
    return
  }

  if (!navigator.onLine) {
    console.info('Application is offline. Skipping sync.')
    return
  }

  dispatch(setSyncInProgress(true))
  const startTime = Date.now()
  try {
    const unSavedOffer = await getUnsavedOffersPromise()
    const currentOffer = state.currentOffer?.data
    const { opportunityId } = currentOffer || {}
    if (unSavedOffer.length > 0) {
      await doSync(unSavedOffer, dispatch, startTime, opportunityId)
    } else {
      dispatch(setUnsavedOfferCount(0))
      dispatch(setSyncInProgress(false))
    }
  } catch (error: ApiError | any) {
    console.error('Sync error:', error)
    dispatch(setSyncInProgress(false))
    const err = error as Error
    throw error instanceof ApiError ? error : new Error(err.message || 'Sync failed with unknown error check console for details')
  }
}

export const syncOfferAsync = wrapCreateAsyncThunk<void, void>('syncOffer/syncUnsavedOffer', syncOfferHandler)

const doSync = async (
  unSyncedOffers: Offer[],
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  startTime: number,
  currentOpportunityId?: string
) => {
  const setLastSyncStartedToNow = (o: Offer): Promise<Offer> =>
    Promise.resolve({ ...o, lastSyncStarted: new Date().toJSON() })

  let successCount = 0
  let failCount = 0
  const errors: ApiError[] = []
  const totalOffers = unSyncedOffers.length

  const syncOffer = async (offer: Offer) => {
    try {
      const syncedOffer = await setLastSyncStartedToNow(offer)
      const serverOffer = await saveOffer(syncedOffer)

      // Fetch the latest client offer from IndexedDB before merging
      const clientOffer = await getOfferByOpportunityIdPromise(offer.opportunityId)

      if (!clientOffer) {
        throw new Error('clientOffer is undefined')
      }

      const clientServerOffer = { clientOffer, serverOffer }
      const mergerdOffer = { ...mergeServerResultsToClientOffer(clientServerOffer), lastSyncStarted: null }
      await updateOfferPromise(mergerdOffer, true)
      if (offer.opportunityId === currentOpportunityId) {
        await dispatch(setOfferData(mergerdOffer))
      }
      successCount++
    } catch (error: ApiError | any) {
      console.error('Error syncing offer:', error)
      failCount++
      errors.push(error)
    } finally {
      if (successCount + failCount === totalOffers) {
        const duration = Date.now() - startTime
        console.info(`Sync complete in ${duration}ms. Success: ${successCount}, Failed: ${failCount}`)
        const allOffers = await getOffersPromise()
        const unsavedCount = unsavedOffersCount(allOffers)
        dispatch(setUnsavedOfferCount(unsavedCount))

        dispatch(setSyncInProgress(false))
      }
    }
  }
  await Promise.all(unSyncedOffers.map(offer => syncOffer(offer)))
  if (errors.length > 0) {
    const error = errors[0]
    throw error
  }
}

const mergeServerResultsToClientOffer = (cso: ClientServerOffer): Offer => {
  const serverOffer = cso.serverOffer
  const clientOffer = cso.clientOffer

  if (isOfferFinalized(serverOffer) || offerIsCompleted(serverOffer)) {
    // offer version is completed or finalized in the server:
    // Always use that because, offer version cannot be modified anymore
    return serverOffer
  }

  if (moment(clientOffer.modified_client).isSameOrBefore(moment(serverOffer.modified_client))) {
    // Nothing on client has changed that could not be overwritten by the server version.
    // Prefer server version.
    return serverOffer
  } else {
    // Local timestamp is newer. This client has the most recent changes to this offer.
    // We therefore retain client version, overwriting what server is sending us.
    // However, we pick couple of attributes from server:
    // id:
    //   this is created on client and used as primary key in server db.
    //   If there are two clients simultaneously creating new version
    //   we might end up with 2 different KCC offer versions.
    //   To ensure only one KCC draft offer, we get id from server.
    // tenderId, tenderName, tenderVersionNumber:
    //   If there is a situation where two versions are creating simultaneously
    //   we might already have salesforce tenderId in server.
    //   This is a corner case, but can cause weird problems later if
    //   tenderId gets overwritten with empty client value
    return {
      ...clientOffer,
      id: serverOffer.id,
      tenderId: serverOffer.tenderId,
      tenderName: serverOffer.tenderName,
      tenderVersionNumber: serverOffer.tenderVersionNumber
    }
  }
}

export async function completeOfferAndScheduleSyncForIt(
  offerToBeSynced: Offer,
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  afterSyncHasStarted: () => void
) {
  const updatedOffer: Offer = {
    ...offerToBeSynced,
    sfTenderVersionActive: false,
    completed: true,
    finalization_started: true,
    modified_client: moment().format()
  }
  await dispatch(updateOfferAsync(_ => updatedOffer))

  const revertOfferUpdate = async () => {
    const revertedOffer: Offer = {
      ...offerToBeSynced,
      sfTenderVersionActive: false,
      completed: false,
      finalization_started: null,
      modified_client: moment().format()
    }
    dispatch(updateOfferAsync(_ => revertedOffer))
    await updateOfferPromise(revertedOffer)
  }
  const offerReadyForSync = (offer: Offer | undefined) =>
    offer && offer.completed && offer.finalization_started && !isOfferFinalized(offer)

  const handleSyncError = async (error: Error) => {
    // Handle sync errors - simplified version without SyncError class
    console.error('Sync error occurred:', error)
    await revertOfferUpdate()
    // Schedule a sync to the next event loop.
    // This way both client and the server will have same understanding of current "reverted situation"
    setTimeout(() => Store.dispatch(syncOfferAsync()), 1)
  }
  const syncAttemptWithRetry = (retriesLeft: number) => async () => {
    if (retriesLeft === 0) {
      console.info(`Scheduled sync for ${offerToBeSynced.opportunityId} cancelled, no more retries left.`)
      return
    }
    try {
      const offers = await getUnsavedOffersPromise()
      const dbOffer = offers.find((o: Offer) => o.opportunityId === offerToBeSynced.opportunityId)
      if (offerReadyForSync(dbOffer)) {
        try {
          await Store.dispatch(syncOfferAsync())
          afterSyncHasStarted()
        } catch (error) {
          await handleSyncError(error as Error)
        }
      } else {
        // Wait little bit longer when not ready
        setTimeout(syncAttemptWithRetry(retriesLeft - 1), 500)
      }
    } catch (error) {
      console.error('Error during sync attempt:', error)
    }
  }
  // Do first round of checks quickly, because indexedDB update should be fast.
  // If there is no success with 5 retries, then we forget this sync.
  setTimeout(syncAttemptWithRetry(5), 50)
}
